import { Icon, Image, type IconNames } from '@petsmart-ui/sparky';
import { useEffect, useRef } from 'react';
import Highlighter from 'react-highlight-words';

import { clsx } from '@libs/common-next';
import { decodeEntityString } from '@libs/algolia';
import type { ListItemProps, PanelItemProps } from '../types';

type AlgoliaHighlightQuery = {
    matchedWords: string[];
};

type AlgoliaHighlightMatchLevel = {
    matchLevel: string;
};

function ListItem({ sourceId, item }: ListItemProps) {
    const iconNames: Record<string, IconNames> = {
        recentSearchesPlugin: 'clock-outline',
        querySuggestionsPlugin: 'search',
    };

    const itemName = item.name ?? item.label ?? item.objectID;
    const matchLevel = (item?._highlightResult?.query as AlgoliaHighlightMatchLevel)?.matchLevel ?? 'none';

    const itemNameAsString: string = decodeURIComponent(encodeURI(itemName as string));

    return (
        <div className="autocomplete-list-item__button-content" data-testid="list-item-suggestion">
            <div className="autocomplete-list-item__main">
                {iconNames[sourceId] && <Icon className="autocomplete-list-item__icon" name={iconNames[sourceId]} />}

                {itemName && (
                    <span className="autocomplete-list-item__name" data-testid="item-name">
                        <Highlighter
                            highlightClassName="normal-text"
                            unhighlightClassName={matchLevel !== 'none' ? 'highlighted-text' : 'normal-text'}
                            searchWords={(item?._highlightResult?.query as AlgoliaHighlightQuery)?.matchedWords ?? []}
                            textToHighlight={itemNameAsString}
                        />
                    </span>
                )}

                {(item.__autocomplete_qsCategory as string) &&
                    !(item.__autocomplete_qsCategory as string).includes('+') && (
                        <span className="autocomplete-list-item__category">
                            — in {item.__autocomplete_qsCategory as string}
                        </span>
                    )}
            </div>

            <Icon className="autocomplete-list-item__icon__arrow autocomplete-assist-search" name="navigation-arrow" />
        </div>
    );
}

function ProductItem({ item }: Readonly<Pick<PanelItemProps, 'item'>>) {
    const imageUrl = item.images?.small ?? item.images?.large;
    return (
        <div className="autocomplete-list-item__product" data-testid="product-item-suggestion">
            {imageUrl && <Image src={imageUrl} alt={`${item.name} image`} loading="lazy" width={64} height={64} />}
            <div className="autocomplete-list-item__main">
                <div className="autocomplete-list-item__product-name">{decodeEntityString(item.name)}</div>
                <div className="autocomplete-list-item__price">
                    {item.price?.formatted.strikethrough
                        ? item.price?.formatted.strikethrough
                        : item.price?.formatted.primary}
                </div>
            </div>
        </div>
    );
}

export function PanelItem({
    className,
    onKeyDown,
    focus,
    item,
    sourceId,
    onClick,
    ...other
}: Readonly<PanelItemProps>) {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        onKeyDown(e);
        if (e.key === 'Enter') {
            e.currentTarget.click();
        }
    };

    const buttonRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (focus) {
            buttonRef.current?.focus();
        } else {
            buttonRef.current?.blur();
        }
    }, [focus]);

    const liClassName = clsx('autocomplete-list-item', className);

    return (
        <li className={liClassName} data-testid="suggestion-item" {...other}>
            <button
                data-testid="suggestion-item-button"
                onClick={onClick}
                className="autocomplete-list-item__button"
                type="submit"
                onKeyDown={handleKeyDown}
                ref={buttonRef}
            >
                {sourceId !== 'productSuggestionsPlugin' ? (
                    <ListItem sourceId={sourceId} item={item} />
                ) : (
                    <ProductItem item={item} />
                )}
            </button>
        </li>
    );
}
